import { Button, TextField, TextFieldProps } from '@mui/material';
import Required from 'components/commons/Required';
import Text from 'components/commons/Text';
import Row from 'components/layouts/Row';
import { useLocale } from 'providers/LocalProvider';
import { translateText } from 'libs/helper/translationHelper';

import React, {
  CSSProperties,
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import { SystemLocaleDto } from 'services/data-contracts';

interface InputFieldProps extends Omit<TextFieldProps, 'onChange'> {
  label?: string;
  value?: Record<string, string>;
  style?: CSSProperties;
  required?: boolean;
  defaultValue?: Record<string, string>;
  useTranslation?: boolean;
  onChange?: (value: Record<string, string>) => void;
}

const LocalizeInput = forwardRef(
  (
    {
      label,
      style,
      required = true,
      defaultValue,
      onChange,
      helperText,
      useTranslation,
      value,
      ...props
    }: InputFieldProps,
    ref: any,
  ) => {
    const inputRefs = useRef<Record<string, HTMLInputElement>>({});
    const isFirstRender = useRef(true);

    const { activeLocale, initLocales } = useLocale();

    const [localValue, setLocalValue] = useState<Record<string, string>>(
      value || defaultValue || {},
    );

    useImperativeHandle(ref, () => ({
      focus,
      getValue: () => localValue,
      setValue: (newValue: Record<string, string>) => {
        setLocalValue(newValue);
      },
    }));

    const focus = () => {
      const firstLocale = activeLocale?.[0]?.locale;
      if (firstLocale) {
        inputRefs.current[firstLocale]?.focus();
      }
    };

    const handleTranslation = async (sourceText: string) => {
      if (!sourceText) return;

      const translations: Record<string, string> = { ...localValue };

      const translationPromises = activeLocale
        ?.filter((lang) => lang.locale !== 'ko')
        ?.map(async (lang) => {
          const locale = lang.locale || '';
          const translatedText = await translateText(sourceText, locale);
          if (translatedText) {
            translations[locale] = translatedText;
          }
        });

      await Promise.all(translationPromises ?? []);
      setLocalValue(translations);
      onChange?.(translations);
    };

    useEffect(() => {
      if (value) {
        setLocalValue(value);
      }
    }, [value]);

    useEffect(() => {
      isFirstRender.current = false;
    }, []);

    return (
      <>
        {useTranslation && (
          <Button onClick={() => handleTranslation(localValue['ko'] ?? '')}>번역</Button>
        )}
        <Row style={{ width: '100%', flex: 1, flexDirection: 'column', gap: 2, ...style }}>
          {activeLocale?.map((language: SystemLocaleDto) => (
            <div key={language.locale} style={{ width: '100%', marginBottom: 10 }}>
              <Row style={{ justifyContent: 'flex-start' }}>
                <Text type="H4" style={{ marginBottom: 10 }}>
                  {language.name} ({language.locale})
                </Text>
                <>{required && <Required />}</>
              </Row>
              <TextField
                inputRef={(el) => {
                  if (el) {
                    inputRefs.current[language.locale ?? ''] = el;
                  }
                }}
                fullWidth
                helperText={helperText}
                value={localValue[language.locale ?? ''] || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newValue = {
                    ...localValue,
                    [language.locale ?? '']: e.target.value,
                  };
                  setLocalValue(newValue);
                  onChange?.(newValue);
                }}
                error={required && !localValue[language.locale ?? '']}
                {...props}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderColor: required && !localValue[language.locale ?? ''] ? 'red' : undefined,
                  },
                  ...props.sx,
                }}
              />
            </div>
          ))}
        </Row>
      </>
    );
  },
);

export default LocalizeInput;
